import React, { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Slide from "@mui/material/Slide";
import { IconButton, TextField, Box } from "@mui/material";
import { Close } from "@mui/icons-material";
import { reportIssue } from "../../services/UserProfileApi";
import { compressImage } from "../../utils/ImageCompression";
import { useSnackbar } from "../../context/SnackbarProvider";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function ReportDialog({ open, onClose }) {
  const { showSnackbar } = useSnackbar();
  const [issue, setIssue] = useState("");
  const [issueDescription, setIssueDescription] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // helper text error message
  const [issueError, setIssueError] = useState("");
  const [issueDescriptionError, setIssueDescriptionError] = useState("");

  // clear the form after submission
  const clearForm = () => {
    setIssue("");
    setIssueDescription("");
    setImages([]);
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleIssueChange = (e) => {
    setIssue(e.target.value);
    if (issueError) setIssueError("");
  };

  const handleIssueDescriptionChange = (e) => {
    setIssueDescription(e.target.value);
    if (issueDescriptionError) setIssueDescriptionError("");
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    const formData = new FormData();
    formData.append("issue", issue);
    formData.append("issue_description", issueDescription);

    try {
      // Compress all images before sending
      const compressedImages = await Promise.all(
        images.map((image) => compressImage(image))
      );

      compressedImages.forEach((image) => {
        formData.append("issue_images[]", image);
      });

      const response = await reportIssue(formData);
      showSnackbar(response.message, "success");
      clearForm();
      document.getElementById("fileInput").value = "";
      onClose();
    } catch (err) {
      console.log(err);
      setIssueError(err.issue || "");
      setIssueDescriptionError(err.issue_description || "");
      setError(err.message || "Failed to submit report");
      showSnackbar("Failed to submit report", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      keepMounted
      onClose={onClose}
      aria-describedby="report-dialog-description"
    >
      <DialogTitle>Report an Issue</DialogTitle>

      <IconButton
        onClick={onClose}
        sx={{ position: "absolute", top: 5, right: 5 }}
      >
        <Close />
      </IconButton>

      <DialogContent>
        <DialogContentText id="report-dialog-description">
          Please fill in the details below to report the issue.
        </DialogContentText>

        <TextField
          fullWidth
          size="small"
          placeholder="Issue Title"
          margin="normal"
          value={issue}
          onChange={handleIssueChange}
          error={Boolean(issueError)}
          helperText={issueError}
        />

        <TextField
          fullWidth
          placeholder="Describe the issue in detail..."
          multiline
          rows={3}
          margin="normal"
          value={issueDescription}
          onChange={handleIssueDescriptionChange}
          error={Boolean(issueDescriptionError)}
          helperText={issueDescriptionError}
        />

        {/* File Upload */}
        <input
          id="fileInput"
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileChange}
        />

        {/* Image Preview with Remove Button */}
        <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {images.map((image, index) => (
            <Box
              key={index}
              sx={{ position: "relative", display: "inline-block" }}
            >
              <img
                src={URL.createObjectURL(image)}
                alt="Selected"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  objectFit: "cover",
                }}
              />
              <IconButton
                size="small"
                onClick={() => removeImage(index)}
                sx={{
                  position: "absolute",
                  top: -5,
                  right: -5,
                  background: "white",
                  color: "red",
                  border: "1px solid red",
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          disableElevation
          sx={{ textTransform: "none" }}
        >
          {loading ? "Submitting..." : "Submit Report"}
        </Button>
      </DialogActions>
      {error && (
        <DialogContentText color="error" sx={{ p: 2, textAlign: "center" }}>
          {error}
        </DialogContentText>
      )}
    </Dialog>
  );
}
